/**
 * Run it live: from a track's Sessions tab to a running room in one card.
 *
 * Shows what the track already holds (who, which items, which criteria), the
 * methods with the recommended one first, and one act: open the room. The
 * readiness the run RPC checks at close is checked here first, so a track that
 * is not ready hears what to do, with the tab named, before anyone votes.
 * The designed, multi-step event keeps its own door ("New session").
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Play, X } from 'lucide-react';
import { useToast } from '@/components/ui/Toast';
import { recordMethodChoice } from '@/lib/methodRecommendationOverrides';
import { pickDefaultMethod, recommendMethods } from '@/lib/sessions/recommendMethods';
import { startRunItLive, type RunBlocker } from '@/lib/sessions/runItLive';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { useUserStore } from '@/stores/prioritization/userStore';
import type { SituationVector } from '@/types/prioritization/situationVector';

export interface RunItLiveCardProps {
  track: { id: string; name: string; cycleId: string | null };
  /** The track's effective participants (cycle and track, people and groups). */
  participantIds: string[];
  /** The track pool's items. */
  itemIds: string[];
  /** The track's accepted criteria, by attribute key. */
  criteriaIds: string[];
  /** The track's weights for those criteria (1047), when the track sets them; else an even split. */
  criteriaWeights?: Record<string, number>;
  /** The room readout, when the cycle recorded one. */
  situationVector?: SituationVector | null;
  /** The tab words the host sees when a readiness step is not met. */
  onGoToTab?: (tab: NonNullable<RunBlocker['tab']>) => void;
  onClose: () => void;
}

const mono: React.CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: '0.55rem',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--text-dim)',
};

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function RunItLiveCard({
  track,
  participantIds,
  itemIds,
  criteriaIds,
  criteriaWeights,
  situationVector,
  onGoToTab,
  onClose,
}: RunItLiveCardProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const userId = useUserStore((s) => s.userId);

  const recommendation = useMemo(
    () =>
      recommendMethods({
        itemCount: itemIds.length,
        situationVector,
        decisionClass: 'S6_ITEM_PRIORITIZATION',
      }),
    [itemIds.length, situationVector],
  );
  const choices = recommendation.choices;
  const [toolId, setToolId] = useState<string>(() => pickDefaultMethod(choices)?.toolId ?? 'stack_ranking');
  useEffect(() => {
    if (!choices.some((c) => c.toolId === toolId)) setToolId(pickDefaultMethod(choices)?.toolId ?? 'stack_ranking');
  }, [choices, toolId]);

  const [busy, setBusy] = useState(false);
  const [blocker, setBlocker] = useState<RunBlocker | null>(null);
  const [error, setError] = useState<string | null>(null);

  // What the host can see is wrong before asking the server.
  const localBlocker: RunBlocker | null =
    itemIds.length === 0
      ? { line: "Put items in the track's pool first.", step: 's2_pool', tab: 'pool' }
      : participantIds.filter((id) => id !== userId).length === 0
        ? { line: 'Add participants to this track first.', step: 's1_participation', tab: 'participation' }
        : null;
  const shownBlocker = blocker ?? localBlocker;

  const others = participantIds.filter((id) => id !== userId).length;
  const chosen = choices.find((c) => c.toolId === toolId);

  const open = async () => {
    if (!userId || busy) return;
    setBusy(true);
    setError(null);
    setBlocker(null);
    const result = await startRunItLive({
      track,
      userId,
      participantIds,
      itemIds,
      criteriaIds,
      criteriaWeights,
      toolId,
      toast: showToast,
    });
    if (!result.ok) {
      setBusy(false);
      if ('blocker' in result) setBlocker(result.blocker);
      else setError(result.error);
      return;
    }
    // The room readout spoke and the host chose: keep the choice against it.
    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId && recommendation.situation.applied) {
      void recordMethodChoice({
        workspaceId,
        sessionId: result.sessionId,
        chosenToolId: toolId,
        recommendation: recommendation.situation,
        vector: situationVector ?? null,
      });
    }
    navigate(result.roomPath);
  };

  return (
    <section
      data-testid="run-it-live-card"
      style={{
        border: '1px solid var(--rule)',
        background: 'var(--bg-raised)',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
        <div>
          <div style={mono}>Run it live</div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.9rem', color: 'var(--text-pri)', marginTop: '4px' }}>
            The room opens on this track's items with the people on it. Phones join from the Board.
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 2 }}
        >
          <X size={14} />
        </button>
      </div>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: '14px',
          rowGap: '4px',
          margin: 0,
          fontFamily: 'var(--sans)',
          fontSize: '0.8rem',
        }}
      >
        <dt style={mono}>Who</dt>
        <dd style={{ margin: 0, color: 'var(--text-sec)' }}>
          {plural(others, 'person', 'people')} from this track, and you as host
        </dd>
        <dt style={mono}>Items</dt>
        <dd style={{ margin: 0, color: 'var(--text-sec)' }}>{plural(itemIds.length, 'item', 'items')} from the track's pool</dd>
        <dt style={mono}>Criteria</dt>
        <dd style={{ margin: 0, color: 'var(--text-sec)' }}>
          {criteriaIds.length > 0
            ? `${plural(criteriaIds.length, 'criterion', 'criteria')} locked on the track`
            : 'none on the track; a scoring method brings its own'}
        </dd>
      </dl>

      <div>
        <div style={{ ...mono, marginBottom: '8px' }}>Method</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '8px' }}>
          {choices.map((c) => {
            const selected = c.toolId === toolId;
            return (
              <button
                key={c.toolId}
                type="button"
                onClick={() => setToolId(c.toolId)}
                disabled={!c.fits}
                title={c.fitNote}
                data-testid={`run-it-live-method-${c.toolId}`}
                aria-pressed={selected}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: `1px solid ${selected ? 'var(--text-pri)' : 'var(--rule)'}`,
                  background: selected ? 'var(--hover-tint)' : 'transparent',
                  cursor: c.fits ? 'pointer' : 'not-allowed',
                  opacity: c.fits ? 1 : 0.5,
                  borderRadius: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                <span style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', color: 'var(--text-pri)', fontWeight: 600 }}>
                  {c.name}
                  {c.recommended && (
                    <span style={{ ...mono, marginLeft: '8px', color: 'var(--green)' }}>recommended</span>
                  )}
                </span>
                <span style={{ fontFamily: 'var(--sans)', fontSize: '0.7rem', color: 'var(--text-ter)' }}>
                  {c.fitNote ?? c.why ?? c.description}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {shownBlocker && (
        <div
          data-testid="run-it-live-blocker"
          style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--mustard)', display: 'flex', gap: '8px', alignItems: 'baseline' }}
        >
          <span>Not yet: {shownBlocker.line}</span>
          {shownBlocker.tab && onGoToTab && (
            <button
              type="button"
              onClick={() => onGoToTab(shownBlocker.tab!)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-sec)', textDecoration: 'underline', fontSize: '0.8rem' }}
            >
              Go there
            </button>
          )}
        </div>
      )}
      {error && (
        <div style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--coral)' }}>{error}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          type="button"
          onClick={() => void open()}
          disabled={busy || !userId || !chosen}
          data-testid="run-it-live-open"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
            fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
            color: '#fff', background: 'var(--green)', border: 'none', borderRadius: '2px',
            cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
          {busy ? 'Opening the room…' : 'Open the room'}
        </button>
        <span style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-ter)' }}>
          Live now. Everyone on the track is told, and the Board shows the code to join.
        </span>
      </div>
    </section>
  );
}
